/**
 * 레거시 `downloadWithPreview.js` 포팅.
 * 파일 URL이 있을 때 다운로드·보기(+선택적 초기화) 버튼을 표시한다.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { fetchRemoteBlob } from '@/shared/utils/downloadImageZip';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Download, Eye } from 'lucide-react';
import React, { useState } from 'react';

/** 레거시 downloadURI — blob으로 받아 파일명 지정 다운로드 */
export const downloadUriFile = async (uri: string, downloadName: string) => {
  const blob = await fetchRemoteBlob(uri);
  const blobUrl = URL.createObjectURL(blob);
  let extension = 'png';
  try {
    extension = new URL(uri).pathname.split('.').pop() || 'png';
  } catch {
    extension = 'png';
  }
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${downloadName}.${extension}`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

/** 레거시 handleViewClick */
export const openFilePreview = (src: string) => {
  window.open(src, '_blank', 'width=1000,height=1000');
};

export interface DownloadWithPreviewProps {
  label: string;
  src?: string | null;
  downloadName: string;
  onReset?: () => void | Promise<void>;
  resetLoading?: boolean;
  resetConfirmTitle?: string;
  resetConfirmDescription?: string;
  /** 보기 버튼 옆에 붙는 추가 액션(OCR 등) */
  actions?: React.ReactNode;
}

export const DownloadWithPreview: React.FC<DownloadWithPreviewProps> = ({
  label,
  src,
  downloadName,
  onReset,
  resetLoading,
  resetConfirmTitle,
  resetConfirmDescription,
  actions,
}) => {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const hasSrc = Boolean(src);

  const handleDownload = async () => {
    if (!src) return;
    setDownloading(true);
    try {
      await downloadUriFile(src, downloadName);
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label>{label}</Label>
        {onReset && hasSrc ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto px-0 text-destructive"
                disabled={resetLoading}
              >
                초기화
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {resetConfirmTitle ?? `${label} 초기화`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {resetConfirmDescription ??
                    '등록된 파일이 삭제됩니다. 초기화하시겠습니까?'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>취소</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={resetLoading}
                  onClick={() => void onReset()}
                >
                  초기화
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasSrc || downloading}
          onClick={() => void handleDownload()}
        >
          <Download className="mr-2 h-4 w-4" />
          다운로드
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasSrc}
          onClick={() => {
            if (src) openFilePreview(src);
          }}
        >
          <Eye className="mr-2 h-4 w-4" />
          보기
        </Button>
        {actions}
      </div>
    </div>
  );
};
